import { Moment } from 'moment'
import React, { useCallback, useMemo } from 'react'

import CreateFlight from '../Create/CreateFlight'
import FlightLookupForm from '../Search/FlightLookup/FlightLookupForm'
import SearchFlights from '../Search/SearchFlights'

import { addToTripClickEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import LineAngleRightIcon from 'components/Luxkit/Icons/line/LineAngleRightIcon'
import LineEditAltIcon from 'components/Luxkit/Icons/line/LineEditAltIcon'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import ListItem from 'components/Luxkit/List/ListItem'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { FareType } from 'constants/flight'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'

interface Props {
  tripId: string
  sectionId?: string
  customOnly?: boolean
  defaultDate?: string
  prefillData?: {
    departureAirport: App.AirportLocation | undefined
    arrivalAirport: App.AirportLocation | undefined
    fareType?: FareType
    checkinDate?: Moment
    checkoutDate?: Moment
  }
}

function SelectFlightType(props: Props) {
  const {
    tripId,
    sectionId,
    prefillData,
    customOnly = false,
    defaultDate,
  } = props

  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()

  const handleSearchFlightClick = useCallback(() => {
    fireInteractionEvent(addToTripClickEvent('modal_flights_search'))
    openModal(SearchFlights, { tripId, sectionId, prefillData, defaultDate })
  }, [openModal, tripId, sectionId, prefillData, defaultDate])

  const handleAddSearchCustomFlightClick = useCallback(() => {
    fireInteractionEvent(addToTripClickEvent('modal_flights_custom'))
    openModal(FlightLookupForm, { tripId, defaultDate }, { size: 'S' })
  }, [openModal, tripId, defaultDate])

  const handleAddCustomFlightClick = useCallback(() => {
    fireInteractionEvent(
      addToTripClickEvent('modal_flights_custom_manual', 'prelookup'),
    )
    openModal(CreateFlight, { tripId, defaultDate })
  }, [defaultDate, openModal, tripId])

  const listItemsData = useMemo(() => {
    return [
      {
        startIcon: <LineSearchIcon />,
        endIcon: <LineAngleRightIcon />,
        key: 'searchFlight',
        title: 'Search flights on Luxury Escapes',
        subtitle: '',
        onClick: handleSearchFlightClick,
      },
      {
        startIcon: <LinePlaneDepartureIcon />,
        endIcon: <LineAngleRightIcon />,
        key: 'searchCustomFlight',
        title: 'Find my flight',
        subtitle: 'Search by flight number',
        onClick: handleAddSearchCustomFlightClick,
      },
      {
        startIcon: <LineEditAltIcon />,
        endIcon: <LineAngleRightIcon />,
        key: 'addCustomFlight',
        title: 'Add your own flight',
        subtitle: 'Enter flight details manually',
        onClick: handleAddCustomFlightClick,
      },
    ].filter((item) => !(customOnly && item.key === 'searchFlight'))
  }, [
    customOnly,
    handleAddCustomFlightClick,
    handleAddSearchCustomFlightClick,
    handleSearchFlightClick,
  ])

  return (
    <>
      <ModalHeader
        title="Flight"
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          {listItemsData.map((item) => (
            <ListItem
              key={item.key}
              startIcon={item.startIcon}
              endIcon={item.endIcon}
              title={item.title}
              subtitle={item.subtitle}
              onClick={item.onClick}
            />
          ))}
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SelectFlightType
